import * as THREE from 'three';
import type { PoseSnapshot, Vec3 } from '../types';
import { logCorrection, logEvent } from './debugLog';

/**
 * Owns the map<->world coordinate relationship.
 *
 * The SDK's ThreeAdapter provides `worldFromMap` (THREE.Matrix4) on every
 * successful localization: it transforms MultiSet map-space points into
 * Three.js world space. All NavMe content (route, markers) is parented under
 * a single `mapRoot` group whose matrix is set to worldFromMap — so content
 * authored in map coordinates renders correctly, and a re-localization only
 * needs to update one matrix.
 */
/** Easing time constant toward a corrected localization. Raised 0.6 → 0.9
 *  (Test 18, "still drifting"): VPS oscillates ±6–8° between fixes; a gentler
 *  ease low-passes those opposing nudges so content settles toward their average
 *  instead of chasing each one — the wide reveal + 25 m pill made that swing
 *  very visible on far content. */
const EASE_TAU_S = 0.9;
/**
 * Rate caps (2026-07-12, "still very unstable" debug session: eased
 * corrections of 1.0–1.6 m / 11–14° every ~4 s while walking): however big
 * the correction, the world may never visibly move faster than this — big
 * corrections glide instead of lurching.
 */
const MAX_SLIDE_M_PER_S = 0.7;
// Lowered 0.35 → 0.18 rad/s (~20°/s → ~10°/s), Test 18 "still drifting": a yaw
// correction pivots the world about the camera, so distant content (crumbs +
// the 25 m destination pill) sweeps ~(angle × distance). Halving the rotational
// glide rate keeps that far-content swing gentle; near content is unaffected.
const MAX_ROT_RAD_PER_S = 0.18; // ~10°/s
/**
 * Deadband: VPS results scatter by roughly this much between queries even
 * when standing still. Corrections smaller than this are measurement noise —
 * applying them makes all AR content visibly wander every few seconds
 * (localization runs every ~4 s). Ignore them; genuine drift accumulates
 * past the deadband and then gets corrected smoothly.
 */
const DEADBAND_TRANSLATION_M = 0.5;
const DEADBAND_ANGLE_RAD = 0.105; // ~6°

/**
 * Field tuning 2026-07-11 (debug report: 11 eased corrections of 1.2–2.5 m /
 * 10–24° in a 90 s session — world visibly slid the whole time): corrections
 * ABOVE this need a second agreeing fix before applying, same machinery as
 * snaps. Lone medium-sized VPS outliers no longer drag the crumbs around.
 */
const MEDIUM_TRANSLATION_M = 1.2;
const MEDIUM_ANGLE_RAD = 0.21; // ~12°
/**
 * A snap-sized correction (see SNAP_* above) may be a genuine relocalization
 * — or a single bad VPS match against a similar-looking area (debug logs
 * showed lone 9 m teleports at decent confidence). Hold such corrections
 * until a SECOND result lands within this radius of the first; two agreeing
 * results are overwhelmingly a real correction.
 */
const CONFIRM_RADIUS_M = 2.5; // must absorb ~4s of walking SLAM drift between queries
/**
 * Escape hatch: if this many consecutive snap-sized corrections arrive
 * without mutually confirming (drift while walking can keep successive
 * estimates > CONFIRM_RADIUS_M apart), the current anchor is clearly stale —
 * apply the latest estimate rather than holding a wrong anchor forever.
 * (Debug logs showed held corrections growing 3m → 10m, never applied.)
 */
const MAX_CONSECUTIVE_HOLDS = 2;
/** Corrections larger than these snap instantly instead of easing (a big jump means the old pose was plain wrong — easing would animate content sliding through the room). */
const SNAP_TRANSLATION_M = 3.0;
const SNAP_ANGLE_RAD = Math.PI / 6; // 30°
/**
 * Field tuning 2026-07-12 (debug report 1783826118637: a conf-0.49 fix
 * snapped the world 3.01 m / 58.2° to a WRONG pose — the next conf-0.92 fix
 * disagreed by 33.6°; the whole session ran at conf 0.49–0.71 and "localized
 * wrongly", per the user). A fix below this confidence may never move the
 * world by a medium/snap-sized amount — it only holds (pending confirmation
 * by a decent-confidence fix) and is NOT eligible for the forced-apply
 * escape hatch. Fixes without a confidence value (WebXR SDK path, which
 * does its own filtering) are treated as trusted.
 */
const MIN_CONF_FOR_BIG_MOVE = 0.6;
/**
 * Anti-teleport-war (2026-07-12 13:56 session, user "AR experience is very
 * poor": snap Δ5.34m at t=49.8, then snap Δ5.67m the OTHER way at t=58.2 —
 * the venue's downstairs returns BIMODAL fixes, pairs alternately
 * supporting two positions ~4.5m apart, and each pair legally earned its
 * 2-fix confirmation). For a while after any snap:
 * - the NEXT snap-sized move needs one extra agreeing fix (3 total), and
 * - even when confirmed it GLIDES (rate-capped) instead of teleporting.
 * A genuine relocation still lands (three agreeing fixes arrive in ~12s);
 * an aliasing flip-flop mostly can't string three together.
 */
const SNAP_COOLDOWN_MS = 20_000;
/**
 * Early-session window (2026-07-12 14:22 session, "no arrow or breadcrumbs
 * at start, then inaccurate"): right after the FIRST fix, the anchor is
 * itself a single unconfirmed estimate — a high-confidence disagreement is
 * MORE trustworthy than the anchor it disagrees with. Within this window,
 * fixes with conf ≥ 0.8 apply immediately (no confirmation holds, no
 * cooldown glide) so the anchor refines fast instead of pinning all AR
 * content to a coarse first fix for 12+ s.
 */
const EARLY_SESSION_MS = 30_000;

export class PoseTransformService {
  /** Parent group for ALL map-anchored content. */
  readonly mapRoot = new THREE.Group();

  private worldFromMap = new THREE.Matrix4();
  private mapFromWorld = new THREE.Matrix4();
  private hasLocalized = false;

  // Smoothed-correction state: current eases toward target each tick().
  private currentPos = new THREE.Vector3();
  private currentQuat = new THREE.Quaternion();
  private targetPos = new THREE.Vector3();
  private targetQuat = new THREE.Quaternion();
  private settled = true;
  /** Held snap-sized correction awaiting confirmation by a second result. */
  private pendingPos: THREE.Vector3 | null = null;
  private consecutiveHolds = 0;
  /** Consecutive fixes agreeing with pendingPos (within CONFIRM_RADIUS_M). */
  private pendingAgree = 0;
  /** Last applied snap: time + world-space delta (oscillation detection). */
  private lastSnapAt = 0;
  private lastSnapDelta: THREE.Vector3 | null = null;
  /** When the FIRST fix of this session landed (early-refinement window). */
  private firstLocalizedAt = 0;

  /**
   * Injectable clock — production uses performance.now(); unit tests drive
   * time deterministically (tests/unit/poseTransform.spec.ts). ALL time
   * logic in this class (cooldowns, early window, oscillation detection)
   * must go through this.now(), never performance.now() directly — the
   * three regressions in TESTING.md §3 (#18→#19→#20) all lived in
   * time-window logic, and this seam is what makes them testable.
   */
  private readonly now: () => number;

  constructor(now: () => number = () => performance.now()) {
    this.now = now;
    this.mapRoot.matrixAutoUpdate = false;
    this.mapRoot.visible = false; // hidden until first localization
  }

  get isLocalized(): boolean {
    return this.hasLocalized;
  }

  /**
   * Call on every successful (re)localization. The first result and large
   * corrections apply instantly; small corrections (SLAM drift being pulled
   * back by background VPS) ease in over ~1 s via tick() so breadcrumbs
   * don't visibly jump while walking.
   */
  setWorldFromMap(worldFromMap: THREE.Matrix4, confidence?: number): void {
    const newPos = new THREE.Vector3();
    const newQuat = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    worldFromMap.decompose(newPos, newQuat, scale);
    const lowConf = confidence !== undefined && confidence < MIN_CONF_FOR_BIG_MOVE;

    const deltaM = this.currentPos.distanceTo(newPos);
    const deltaDeg = (this.currentQuat.angleTo(newQuat) * 180) / Math.PI;
    // SIGNED yaw delta (2026-07-12): magnitudes alone can't distinguish
    // steady one-way SLAM drift from VPS oscillating between two solutions
    // (+12°, −12°, +12°…) — the sign pattern in the log tells them apart.
    const fwdCur = new THREE.Vector3(0, 0, -1).applyQuaternion(this.currentQuat);
    const fwdNew = new THREE.Vector3(0, 0, -1).applyQuaternion(newQuat);
    let yawDelta =
      ((Math.atan2(fwdNew.x, fwdNew.z) - Math.atan2(fwdCur.x, fwdCur.z)) * 180) / Math.PI;
    yawDelta = ((yawDelta + 540) % 360) - 180;
    const yawStr = `yaw ${yawDelta >= 0 ? '+' : ''}${yawDelta.toFixed(1)}°`;

    // Deadband: within-noise corrections are dropped entirely.
    if (
      this.hasLocalized &&
      deltaM < DEADBAND_TRANSLATION_M &&
      this.currentQuat.angleTo(newQuat) < DEADBAND_ANGLE_RAD
    ) {
      this.pendingPos = null;
      logCorrection('deadband', deltaM, deltaDeg);
      return;
    }

    const bigJump =
      !this.hasLocalized ||
      deltaM > SNAP_TRANSLATION_M ||
      this.currentQuat.angleTo(newQuat) > SNAP_ANGLE_RAD;
    const mediumJump =
      bigJump ||
      deltaM > MEDIUM_TRANSLATION_M ||
      this.currentQuat.angleTo(newQuat) > MEDIUM_ANGLE_RAD;

    // Snap-sized corrections need a second, agreeing result before applying —
    // a lone bad VPS match must not teleport the world. But never hold more
    // than MAX_CONSECUTIVE_HOLDS in a row: while walking, successive
    // estimates legitimately disagree by the drift accrued between queries,
    // and an anchor that keeps producing snap-sized deltas is stale anyway.
    if (this.hasLocalized && mediumJump) {
      // A low-confidence fix may not apply a medium/snap-sized move, PERIOD
      // — it records itself as pending (so a later good fix can confirm the
      // location) but neither counts toward nor consumes the forced-apply
      // escape. See MIN_CONF_FOR_BIG_MOVE rationale.
      if (lowConf) {
        this.pendingPos = (this.pendingPos ?? new THREE.Vector3()).copy(newPos);
        logEvent(
          'pose',
          `low-conf (${confidence!.toFixed(2)}) Δ${deltaM.toFixed(2)}m ${deltaDeg.toFixed(1)}° (${yawStr}) held — needs conf ≥${MIN_CONF_FOR_BIG_MOVE} to move the world`,
        );
        return;
      }
      // EARLY-SESSION REFINEMENT: within 30s of the first fix, a
      // high-confidence disagreement outranks the single-fix anchor —
      // apply immediately (no holds; see EARLY_SESSION_MS rationale).
      // MAGNITUDE CAP (2026-07-12 20:53 session, "I don't see breadcrumbs"):
      // a lone conf-0.90 outlier applied Δ8.12m/48.6° instantly and rotated
      // the whole route out of the user's view — the NEXT fix wanted the
      // exact reversal (+46.9°), proving it wrong. Plausible first-fix
      // refinements are metres-scale (14:22 session: 3.5-3.9m); anything
      // beyond ~4m/25° is more likely an aliased match than a refinement,
      // so it goes through normal confirmation even in the early window.
      const early =
        this.now() - this.firstLocalizedAt < EARLY_SESSION_MS &&
        (confidence === undefined || confidence >= 0.8) &&
        deltaM <= 4.0 &&
        this.currentQuat.angleTo(newQuat) <= (25 * Math.PI) / 180;
      if (early) {
        logEvent(
          'pose',
          `early-session refinement (anchor ${((this.now() - this.firstLocalizedAt) / 1000).toFixed(0)}s old, conf ${confidence?.toFixed(2) ?? 'n/a'}) — applying Δ${deltaM.toFixed(2)}m ${deltaDeg.toFixed(1)}° immediately`,
        );
        this.pendingPos = null;
        this.pendingAgree = 0;
        this.consecutiveHolds = 0;
      } else {
      // BUGFIX (2026-07-12 14:22 session): lastSnapAt starts at 0, and
      // `now - 0 < 20000` made the first 20s of EVERY page load count as
      // "post-snap cooldown" — startup corrections were held for 12+s.
      // A cooldown only exists after a real snap (lastSnapAt > 0).
      const inCooldown =
        this.lastSnapAt > 0 && this.now() - this.lastSnapAt < SNAP_COOLDOWN_MS;
      const agrees =
        this.pendingPos !== null && this.pendingPos.distanceTo(newPos) <= CONFIRM_RADIUS_M;
      this.pendingAgree = agrees ? this.pendingAgree + 1 : 0;
      this.pendingPos = (this.pendingPos ?? new THREE.Vector3()).copy(newPos);
      // Normally the 2nd agreeing fix applies; shortly after a snap, the 3rd
      // (see SNAP_COOLDOWN_MS — anti flip-flop).
      const needAgree = inCooldown ? 2 : 1;
      if (this.pendingAgree < needAgree) {
        this.consecutiveHolds++;
        // Escape hatch: a stale anchor produces endless disagreeing
        // snap-sized deltas — apply eventually rather than hold forever.
        // Within the cooldown the escape is slower (oscillating fixes must
        // not be able to force their way through).
        const maxHolds = inCooldown ? 5 : MAX_CONSECUTIVE_HOLDS;
        if (this.consecutiveHolds <= maxHolds) {
          logEvent(
            'pose',
            `held Δ${deltaM.toFixed(2)}m ${deltaDeg.toFixed(1)}° (${yawStr}) — awaiting confirmation (agree ${this.pendingAgree}/${needAgree}${inCooldown ? ', post-snap cooldown' : ''})`,
          );
          return;
        }
        logEvent('pose', 'forced apply after repeated holds — anchor stale');
      }
      this.pendingPos = null; // confirmed (or forced) — fall through and apply
      this.pendingAgree = 0;
      this.consecutiveHolds = 0;
      }
    } else {
      this.pendingPos = null;
      this.pendingAgree = 0;
      this.consecutiveHolds = 0;
    }

    this.targetPos.copy(newPos);
    this.targetQuat.copy(newQuat);

    if (this.hasLocalized) {
      logCorrection(bigJump ? 'snap' : 'eased', deltaM, deltaDeg, confidence);
      logEvent('pose', `correction detail: ${yawStr}, pivot=camera`);
    }

    if (bigJump) {
      const now = this.now();
      // Cooldown glide only after a REAL snap (lastSnapAt > 0 — see bugfix
      // above) and never during the early-refinement window: session-start
      // refinements must land instantly, not glide for seconds.
      const earlyWindow = now - this.firstLocalizedAt < EARLY_SESSION_MS;
      const withinSnapCooldown =
        this.hasLocalized &&
        !earlyWindow &&
        this.lastSnapAt > 0 &&
        now - this.lastSnapAt < SNAP_COOLDOWN_MS;
      if (this.hasLocalized) {
        // Oscillation detector: a snap that largely REVERSES the previous
        // one means two map regions are aliasing (VPS returns bimodal
        // fixes). Software can only damp it — the area needs a re-scan.
        const delta = new THREE.Vector3().subVectors(newPos, this.currentPos);
        if (this.lastSnapDelta && now - this.lastSnapAt < 25_000) {
          const dot = delta.dot(this.lastSnapDelta);
          if (dot < -0.3 * delta.length() * this.lastSnapDelta.length()) {
            logEvent(
              'pose',
              `⚠️ VPS OSCILLATION: this snap (Δ${delta.length().toFixed(1)}m) largely reverses the previous one (Δ${this.lastSnapDelta.length().toFixed(1)}m, ${((now - this.lastSnapAt) / 1000).toFixed(0)}s ago) — two similar-looking map regions are aliasing here; RE-SCAN this area`,
            );
          }
        }
        this.lastSnapDelta = delta;
        this.lastSnapAt = now;
      }
      if (withinSnapCooldown) {
        // Second snap-sized move right after a snap: GLIDE it in (rate-
        // capped) instead of teleporting — teleport wars (Δ5.3m one way,
        // Δ5.7m back, 9s apart) are the worst experience in the logs.
        this.settled = false;
        logEvent('pose', 'snap-sized correction within post-snap cooldown — gliding instead of teleporting');
      } else {
        this.currentPos.copy(this.targetPos);
        this.currentQuat.copy(this.targetQuat);
        this.applyCurrent();
        this.settled = true;
      }
    } else {
      this.settled = false; // tick() eases toward the target
    }

    this.mapRoot.visible = true;
    if (!this.hasLocalized) this.firstLocalizedAt = this.now();
    this.hasLocalized = true;
    this.lastCorrectionAt = this.now();
    this.lastCorrectionM = deltaM;
  }

  private lastCorrectionAt = 0;
  private lastCorrectionM = 0;

  /**
   * True while a recent world correction ≥ minM is still "fresh". Route
   * logic uses this to avoid reacting to the correction itself: an eased
   * 2 m world shift looks exactly like the user teleporting 2 m, which used
   * to fire spurious off-route recalcs and premature arrivals.
   */
  recentCorrection(minM = 1.0, windowMs = 3000): boolean {
    return this.lastCorrectionM >= minM && this.now() - this.lastCorrectionAt < windowMs;
  }

  /**
   * True shortly after a SNAP-sized correction was applied. The user's real
   * position is uncertain right after a big relocation (and during an
   * aliasing flip-flop it's actively contested) — off-route recalcs in this
   * window replace a good route from a possibly-wrong position (2026-07-12
   * 13:56: recalc fired 8s after a snap, from the contested position).
   */
  recentSnap(windowMs = 12_000): boolean {
    return this.lastSnapAt > 0 && this.now() - this.lastSnapAt < windowMs;
  }

  /** True while a correction is still gliding in (rate-capped easing). */
  get isSettling(): boolean {
    return this.hasLocalized && !this.settled;
  }

  /**
   * #28 (Test 5): true while an unconfirmed VPS correction is being HELD —
   * the localizer keeps returning a position that disagrees with the displayed
   * anchor, so the pose is contested and its heading is not trustworthy. Any
   * apply/deadband clears the pending hold, so a well-tracked pose reads false.
   * Consumed by the G6 turn-around gate (never command a reverse off a
   * contested anchor).
   */
  get holdingCorrection(): boolean {
    return this.pendingPos !== null;
  }

  private lastTickAt = 0;

  /**
   * Call once per rendered frame (cheap no-op when settled). Pass the camera
   * to enable CAMERA-PIVOT easing (2026-07-12, "still very unstable" — the
   * decisive fix): the old code interpolated the map ORIGIN's pose, so a 12°
   * rotational correction pivoted the whole world about a point ~10 m from
   * the user — crumbs 5 m ahead swept sideways by a metre+ even though the
   * fix AGREED about where the user was standing. Now rotation pivots about
   * the CAMERA: the floor under the user stays put (moves only by the local
   * positional error), and the sweep happens far away where the progressive
   * crumb reveal hides it.
   */
  tick(camera?: THREE.Camera): void {
    if (this.settled || !this.hasLocalized) return;
    const now = this.now();
    const dt = Math.min(0.1, Math.max(0.008, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;
    const k = 1 - Math.exp(-dt / EASE_TAU_S);

    // Rotation step, rate-capped.
    const angRemain = this.currentQuat.angleTo(this.targetQuat);
    const rotStep = angRemain > 1e-6 ? Math.min(k, (MAX_ROT_RAD_PER_S * dt) / angRemain) : 1;
    const qNext = this.currentQuat.clone().slerp(this.targetQuat, Math.min(1, rotStep));

    if (camera) {
      const camWorld = camera.getWorldPosition(new THREE.Vector3());
      // Map point currently under the camera; its world position is camWorld
      // by construction, and under the TARGET transform it lands at wTarget.
      const mapUnderCam = camWorld.clone().applyMatrix4(this.mapFromWorld);
      const targetMatrix = new THREE.Matrix4().compose(
        this.targetPos,
        this.targetQuat,
        new THREE.Vector3(1, 1, 1),
      );
      const wTarget = mapUnderCam.clone().applyMatrix4(targetMatrix);
      const move = wTarget.sub(camWorld); // local error AT the user
      const dist = move.length();
      const posStep = dist > 1e-6 ? Math.min(k, (MAX_SLIDE_M_PER_S * dt) / dist) : 1;
      const wNext = camWorld.clone().addScaledVector(move, Math.min(1, posStep));
      this.currentQuat.copy(qNext);
      // Solve the origin position so the camera's map point lands at wNext
      // under the new rotation: p = wNext − R·m_c. Converges EXACTLY to the
      // target matrix (at rot=target, wNext=wTarget ⇒ p = targetPos).
      this.currentPos.copy(wNext).sub(mapUnderCam.clone().applyQuaternion(this.currentQuat));
    } else {
      this.currentPos.lerp(this.targetPos, k);
      this.currentQuat.copy(qNext);
    }

    if (
      this.currentPos.distanceTo(this.targetPos) < 0.01 &&
      this.currentQuat.angleTo(this.targetQuat) < 0.005
    ) {
      this.currentPos.copy(this.targetPos);
      this.currentQuat.copy(this.targetQuat);
      this.settled = true;
    }
    this.applyCurrent();
  }

  private applyCurrent(): void {
    this.worldFromMap.compose(this.currentPos, this.currentQuat, new THREE.Vector3(1, 1, 1));
    this.mapFromWorld.copy(this.worldFromMap).invert();
    this.mapRoot.matrix.copy(this.worldFromMap);
    this.mapRoot.matrixWorldNeedsUpdate = true;
  }

  /** Hide map-anchored content (e.g. tracking lost / session ended). */
  reset(): void {
    this.hasLocalized = false;
    this.mapRoot.visible = false;
  }

  mapToWorld(p: Vec3): THREE.Vector3 {
    return new THREE.Vector3(p.x, p.y, p.z).applyMatrix4(this.worldFromMap);
  }

  worldToMap(p: THREE.Vector3): Vec3 {
    const v = p.clone().applyMatrix4(this.mapFromWorld);
    return { x: v.x, y: v.y, z: v.z };
  }

  /** Current camera pose expressed in both spaces. */
  snapshot(camera: THREE.Camera): PoseSnapshot {
    const worldPos = new THREE.Vector3();
    camera.getWorldPosition(worldPos);

    const mapPos = this.worldToMap(worldPos);

    // Heading: camera forward projected onto XZ in map space.
    const fwdWorld = new THREE.Vector3(0, 0, -1).applyQuaternion(
      camera.getWorldQuaternion(new THREE.Quaternion()),
    );
    const originMap = this.worldToMap(worldPos);
    const aheadMap = this.worldToMap(worldPos.clone().add(fwdWorld));
    const headingRad = Math.atan2(aheadMap.x - originMap.x, -(aheadMap.z - originMap.z));

    return {
      mapPosition: mapPos,
      worldPosition: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
      headingRad,
    };
  }

  /**
   * Raycasts from the camera through a screen point (NDC, -1..1) against the
   * map's horizontal floor plane (map-space y = `mapFloorY`, default 0 to
   * match every sample POI/node's convention of y=0 = floor level) and
   * returns the hit point in map space, or null if the ray never crosses
   * that plane (e.g. pointing above the horizon).
   *
   * This is a plain-geometry raycast (THREE.Raycaster + THREE.Plane), not a
   * real depth/plane-detection hit-test — neither the WebXR session nor the
   * 8th Wall camera pipeline is configured for a `hit-test` feature in this
   * app, so there's no actual surface awareness. It's a v1 heuristic for
   * "tap to place a POI on the floor": accurate on a single flat floor,
   * wrong if you tap on a raised surface (e.g. a table) expecting a point at
   * table height — it'll place the point at true floor level instead.
   */
  raycastFloor(camera: THREE.Camera, ndcX: number, ndcY: number, mapFloorY = 0): Vec3 | null {
    if (!this.hasLocalized) return null;

    const worldOrigin = this.mapToWorld({ x: 0, y: mapFloorY, z: 0 });
    const worldNormal = new THREE.Vector3(0, 1, 0)
      .transformDirection(this.worldFromMap)
      .normalize();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(worldNormal, worldOrigin);

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const hit = new THREE.Vector3();
    const found = raycaster.ray.intersectPlane(plane, hit);
    if (!found) return null;
    return this.worldToMap(hit);
  }
}
